import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar,
  Clock,
  User,
  Stethoscope,
  Plus,
  X,
  Check,
  AlertTriangle,
  Loader2,
  RefreshCw,
  ArrowLeft,
  Building,
  Phone,
  Mail
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useTranslation } from '../contexts/TranslationContext';
import { getAllResidents } from '../lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  scheduled: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  confirmed: 'bg-green-500/10 text-green-500 border-green-500/30',
  completed: 'bg-[#52525b]/20 text-[#a1a1aa] border-[#52525b]/30',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/30',
};

function AppointmentCard({ appointment, residents, onStatusChange, onDelete, t }) {
  const resident = residents.find(r => r.id === appointment.resident_id);
  
  const appointmentTypes = [
    { value: 'general', label: t('general_checkup'), icon: Stethoscope },
    { value: 'specialist', label: t('specialist_visit'), icon: User },
    { value: 'followup', label: t('followup'), icon: Calendar },
    { value: 'emergency', label: t('emergency'), icon: AlertTriangle },
  ];
  
  const typeConfig = appointmentTypes.find(type => type.value === appointment.type) || appointmentTypes[0];
  const TypeIcon = typeConfig.icon;

  return (
    <div 
      className="bg-[var(--eldera-card)] border border-[var(--eldera-border)] rounded-xl p-4 sm:p-5 hover:border-blue-500/30 transition-all"
      data-testid={`appointment-card-${appointment.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <TypeIcon className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h3 className="font-medium text-sm">{typeConfig.label}</h3>
            <p className="text-xs text-[var(--eldera-muted)]">
              {resident ? `${resident.first_name} ${resident.last_name}` : 'Unknown Resident'}
            </p>
          </div>
        </div>
        <Badge variant="outline" className={cn("text-[10px]", statusColors[appointment.status])}>
          {appointment.status}
        </Badge>
      </div>
      
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4 text-[var(--eldera-subtle)]" />
          <span>{new Date(appointment.date).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-[var(--eldera-subtle)]" />
          <span>{appointment.time}</span>
        </div>
        {appointment.doctor_name && (
          <div className="flex items-center gap-2 text-sm">
            <Stethoscope className="h-4 w-4 text-[var(--eldera-subtle)]" />
            <span>Dr. {appointment.doctor_name}</span>
          </div>
        )}
      </div>
      
      {appointment.notes && (
        <p className="text-xs text-[var(--eldera-muted)] bg-[var(--eldera-bg)] p-2 rounded mb-4">
          {appointment.notes}
        </p>
      )}
      
      <div className="flex items-center gap-2 pt-3 border-t border-[var(--eldera-border)]">
        {appointment.status === 'scheduled' && (
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => onStatusChange(appointment.id, 'confirmed')}
            className="text-xs h-8"
          >
            <Check className="h-3 w-3 mr-1" />
            {t('confirm')}
          </Button>
        )}
        {(appointment.status === 'scheduled' || appointment.status === 'confirmed') && (
          <>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onStatusChange(appointment.id, 'completed')}
              className="text-xs h-8 border-green-500/30 text-green-500 hover:bg-green-500/10"
            >
              {t('complete')}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => onStatusChange(appointment.id, 'cancelled')}
              className="text-xs h-8 border-red-500/30 text-red-500 hover:bg-red-500/10"
            >
              {t('cancel')}
            </Button>
          </>
        )}
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => onDelete(appointment.id)}
          className="text-xs h-8 ml-auto text-[var(--eldera-subtle)] hover:text-red-500"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

function NewAppointmentDialog({ residents, onCreated, t }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    resident_id: '',
    type: 'general',
    date: '',
    time: '09:00',
    doctor_name: '',
    notes: ''
  });
  
  const appointmentTypes = [
    { value: 'general', label: t('general_checkup'), icon: Stethoscope },
    { value: 'specialist', label: t('specialist_visit'), icon: User },
    { value: 'followup', label: t('followup'), icon: Calendar },
    { value: 'emergency', label: t('emergency'), icon: AlertTriangle },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.resident_id || !form.date) {
      toast.error(t('select_resident'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(t('scheduled'));
        setOpen(false);
        setForm({
          resident_id: '',
          type: 'general',
          date: '',
          time: '09:00',
          doctor_name: '',
          notes: ''
        });
        onCreated();
      } else {
        toast.error('Error');
      }
    } catch (error) {
      toast.error('Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-appointment-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t('new_appointment')}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--eldera-card)] border-[var(--eldera-border)] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-500" />
            {t('book_appointment')}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label className="text-sm">{t('select_resident')} *</Label>
            <Select 
              value={form.resident_id} 
              onValueChange={(value) => setForm(prev => ({ ...prev, resident_id: value }))}
            >
              <SelectTrigger className="bg-[var(--eldera-bg)] border-[var(--eldera-border)] mt-1" data-testid="select-resident">
                <SelectValue placeholder={t('select_resident')} />
              </SelectTrigger>
              <SelectContent className="bg-[var(--eldera-card)] border-[var(--eldera-border)]">
                {residents.map(r => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.first_name} {r.last_name} ({r.apartments?.apt_label || 'No apt'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-sm">{t('appointment_type')}</Label>
            <Select 
              value={form.type} 
              onValueChange={(value) => setForm(prev => ({ ...prev, type: value }))}
            >
              <SelectTrigger className="bg-[var(--eldera-bg)] border-[var(--eldera-border)] mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--eldera-card)] border-[var(--eldera-border)]">
                {appointmentTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">{t('date')} *</Label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => setForm(prev => ({ ...prev, date: e.target.value }))}
                className="bg-[var(--eldera-bg)] border-[var(--eldera-border)] mt-1"
                min={new Date().toISOString().split('T')[0]}
                data-testid="appointment-date"
              />
            </div>
            <div>
              <Label className="text-sm">{t('time')}</Label>
              <Input
                type="time"
                value={form.time}
                onChange={(e) => setForm(prev => ({ ...prev, time: e.target.value }))}
                className="bg-[var(--eldera-bg)] border-[var(--eldera-border)] mt-1"
                data-testid="appointment-time"
              />
            </div>
          </div>
          
          <div>
            <Label className="text-sm">{t('doctor_name')} (optional)</Label>
            <Input
              value={form.doctor_name}
              onChange={(e) => setForm(prev => ({ ...prev, doctor_name: e.target.value }))}
              placeholder="Dr. Smith"
              className="bg-[var(--eldera-bg)] border-[var(--eldera-border)] mt-1"
            />
          </div>
          
          <div>
            <Label className="text-sm">{t('notes')} (optional)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="..."
              className="bg-[var(--eldera-bg)] border-[var(--eldera-border)] mt-1 min-h-[80px]"
            />
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
              {t('cancel')}
            </Button>
            <Button type="submit" disabled={loading} className="flex-1" data-testid="submit-appointment">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
              {t('book_appointment')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Appointments() {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [appointmentsRes, residentsData] = await Promise.all([
        fetch(`${BACKEND_URL}/api/appointments`),
        getAllResidents()
      ]);
      
      if (appointmentsRes.ok) {
        const data = await appointmentsRes.json();
        setAppointments(data);
      }
      setResidents(residentsData || []);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusChange = async (id, status) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/appointments/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success(`Appointment ${status}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/appointments/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Appointment deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const filteredAppointments = appointments.filter(apt => {
    if (filter === 'all') return true;
    return apt.status === filter;
  });

  const stats = {
    total: appointments.length,
    scheduled: appointments.filter(a => a.status === 'scheduled').length,
    confirmed: appointments.filter(a => a.status === 'confirmed').length,
    completed: appointments.filter(a => a.status === 'completed').length,
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 pt-16 sm:pt-4" data-testid="appointments-page">
      {/* Header */}
      <Link to="/" className="flex items-center gap-2 text-[var(--eldera-muted)] hover:text-[var(--eldera-text)] mb-4 sm:mb-6">
        <ArrowLeft className="h-4 w-4" />
        {t('back_to_dashboard')}
      </Link>
      
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-barlow font-bold text-xl sm:text-2xl md:text-3xl tracking-tight flex items-center gap-3">
            <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
            {t('medical_appointments')}
          </h1>
          <p className="text-[var(--eldera-muted)] text-sm mt-1">
            {t('manage_appointments')}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
            {t('refresh')}
          </Button>
          <NewAppointmentDialog residents={residents} onCreated={fetchData} t={t} />
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-[var(--eldera-card)] border border-[var(--eldera-border)] rounded-xl p-4">
          <div className="text-2xl font-bold font-mono">{stats.total}</div>
          <div className="text-xs text-[var(--eldera-muted)]">{t('total')}</div>
        </div>
        <div className="bg-[var(--eldera-card)] border border-[var(--eldera-border)] rounded-xl p-4">
          <div className="text-2xl font-bold font-mono text-blue-500">{stats.scheduled}</div>
          <div className="text-xs text-[var(--eldera-muted)]">{t('scheduled')}</div>
        </div>
        <div className="bg-[var(--eldera-card)] border border-[var(--eldera-border)] rounded-xl p-4">
          <div className="text-2xl font-bold font-mono text-green-500">{stats.confirmed}</div>
          <div className="text-xs text-[var(--eldera-muted)]">{t('confirmed')}</div>
        </div>
        <div className="bg-[var(--eldera-card)] border border-[var(--eldera-border)] rounded-xl p-4">
          <div className="text-2xl font-bold font-mono text-[var(--eldera-subtle)]">{stats.completed}</div>
          <div className="text-xs text-[var(--eldera-muted)]">{t('completed')}</div>
        </div>
      </div>
      
      {/* Filter */}
      <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
        {['all', 'scheduled', 'confirmed', 'completed', 'cancelled'].map(status => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
            className="capitalize whitespace-nowrap"
          >
            {t(status)}
          </Button>
        ))}
      </div>
      
      {/* Appointments Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : filteredAppointments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAppointments.map(appointment => (
            <AppointmentCard
              key={appointment.id}
              appointment={appointment}
              residents={residents}
              onStatusChange={handleStatusChange}
              onDelete={handleDelete}
              t={t}
            />
          ))}
        </div>
      ) : (
        <div className="bg-[var(--eldera-card)] border border-[var(--eldera-border)] rounded-xl p-8 text-center">
          <Calendar className="h-12 w-12 text-[var(--eldera-subtle)] mx-auto mb-4" />
          <h3 className="font-barlow font-bold text-lg mb-2">{t('no_appointments')}</h3>
          <p className="text-[var(--eldera-muted)] text-sm mb-4">
            {t('manage_appointments')}
          </p>
          <NewAppointmentDialog residents={residents} onCreated={fetchData} t={t} />
        </div>
      )}
    </div>
  );
}
